import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";

export default function Faq() {
  const [hovered, setHovered] = useState("faq");
  const [openIndex, setOpenIndex] = useState(null);

  const breadcrumbItems = [
    {
      label: "Home",
      active: hovered === "home",
      onClick: () => {},
      onMouseEnter: () => setHovered("home"),
      onMouseLeave: () => setHovered(null),
    },
    {
      label: "FAQ",
      active: hovered === "faq" || hovered === null,
      onClick: () => {},
      onMouseEnter: () => setHovered("faq"),
      onMouseLeave: () => setHovered("faq"),
    },
  ];

  const faqSections = [
    {
      section: "About VeriMED",
      faqs: [
        {
          question: "1. What is VeriMED?",
          answer:
            "VeriMED is Cameroon’s first concierge-style home health and care navigation service. We bridge distance by providing international-standard medical care at home—giving peace of mind to families living abroad who want reliable, compassionate, and competent care for their loved ones in Cameroon.",
        },
        {
          question: "2. Who do you serve?",
          answer:
            "We serve older adults, chronically ill patients, and recovering clients in Cameroon—especially those whose children or responsible parties live abroad in the USA, UK, Canada, or Europe.",
        },
        {
          question:
            "3. What makes VeriMED different from a traditional clinic or home care agency?",
          answer:
            "We combine virtual care, in-home physician and nurse visits, and care navigation under one umbrella. Each client is assigned a care manager who coordinates services, provides regular updates to families, and ensures that global standards of care are met.",
        },
      ],
    },
    {
      section: "Services & Membership",
      faqs: [
        {
          question: "4. What services does VeriMED offer?",
          answer:
            "Our services include physician home and virtual consultations, skilled nurse visits and wellness checks, IV therapy and rehydration at home, live-in family wellness nurse service, diabetes and chronic disease management, post-surgery recovery, hospital-at-home programs, and concierge membership plans for ongoing family communication.",
        },
        {
          question: "5. How do I choose the right membership plan?",
          answer:
            "We offer four membership tiers—Essential, Premium, Elite, and Platinum—based on your loved one’s level of need. Each plan includes a dedicated care manager, family updates, and prioritized scheduling. Our team can recommend a plan or provide a one-month trial if you’re unsure.",
        },
        {
          question:
            "6. Can families living abroad pay directly for their parents’ care?",
          answer:
            "Yes. Our system is designed for diaspora sponsors. You can pay securely online, receive itemized receipts, and get monthly or weekly reports, depending on your plan.",
        },
      ],
    },
    {
      section: "Communication & Family Updates",
      faqs: [
        {
          question:
            "7. How will I receive updates about my loved one’s condition?",
          answer:
            "Depending on your membership level, you’ll receive monthly or weekly WhatsApp or email updates, short video summaries (optional), and secure virtual meetings when important changes occur.",
        },
        {
          question: "8. What happens if I can’t reach the team right away?",
          answer:
            "While we strive to respond within 4 hours during business days, there may be brief delays due to local power or internet interruptions. Your care manager will contact you as soon as service is restored. Emergency numbers remain available for urgent medical concerns.",
        },
      ],
    },
    {
      section: "Reliability & Response Time",
      faqs: [
        {
          question:
            "9. How do you manage delays caused by power cuts or slow internet?",
          answer:
            "We operate from multiple network providers and maintain backup systems. When connectivity issues occur, updates are sent through the fastest available method (WhatsApp, SMS, or call). Families are notified once full service resumes.",
        },
        {
          question:
            "10. How quickly can I expect a home visit after making a request?",
          answer:
            "For scheduled care, visits are confirmed within 48 business hours. For urgent same-day requests, we dispatch a physician or nurse as soon as possible—typically within 4–6 hours, depending on location and road conditions.",
        },
      ],
    },
    {
      section: "Clinical & Quality Standards",
      faqs: [
        {
          question: "11. Are VeriMED clinicians licensed and trained?",
          answer:
            "Absolutely. All our physicians and nurses are licensed in Cameroon and receive ongoing training to meet international care standards under our W.E.C.A.R.E. values — World-Class Standards, Empathy-Driven Service, and Clinical Competence.",
        },
        {
          question: "12. How does VeriMED ensure care quality and safety?",
          answer:
            "We monitor key indicators such as 4-hour response time, 98% visit documentation, 90% updated vitals, and 95% of alerts handled promptly. We also conduct periodic case reviews and family satisfaction surveys.",
        },
      ],
    },
    {
      section: "Payments & Billing",
      faqs: [
        {
          question: "13. How do I make payments?",
          answer:
            "Payments can be made online through our secure platform. Receipts are issued and suitable for insurance reimbursement.",
        },
        {
          question: "14. Are medications and lab tests included in visit fees?",
          answer:
            "No. Medications and lab tests are billed separately with itemized receipts from our verified partners.",
        },
        {
          question:
            "15. Is there an additional charge for after-hours or weekend visits?",
          answer:
            "Yes. Visits after 4 PM, on weekends, or holidays incur an after-hours fee of 35,000 CFA.",
        },
      ],
    },
    {
      section: "Technology & Access",
      faqs: [
        {
          question: "16. Do you have a patient app?",
          answer:
            "Yes, our VeriMED Connect App allows families to view visit summaries and lab reports securely. Clients can access all information via our patient and sponsor portal.",
        },
        {
          question: "17. What if my parent lives outside the main city?",
          answer:
            "We currently serve Buea, Kumba, Limbe, Tiko, and Mutengene for in-person visits. Virtual services are available nationwide, with a small travel surcharge for areas outside city limits.",
        },
      ],
    },
    {
      section: "Peace of Mind",
      faqs: [
        {
          question: "18. What if my loved one has an emergency?",
          answer:
            "Our care teams provide emergency coordination — arranging urgent visits, coordinating hospital transport, and notifying sponsors immediately. For Elite and Platinum members, this service is included.",
        },
        {
          question: "19. How does VeriMED support families emotionally?",
          answer:
            "Beyond medical care, we offer empathy-driven communication. Our nurses check in with families abroad to discuss progress, challenges, and next steps — because peace of mind is part of healthcare.",
        },
      ],
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  let count = 0; // for unique question numbering

  return (
    <div className="faqPageContainer">
      <Header />

      {/* HERO SECTION */}
      <div className="faqHero">
        <div className="faqBreadcrumbWrapper">
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <div className="faqHeroBg" />

        <div className="faqHeroText">
          <p className="faqSubtitle">FAQ</p>
          <h1 className="faqTitle">Frequently Asked Questions</h1>
        </div>
      </div>

      {/* FAQ LIST */}
      <div className="faqContentWrapper">
        {faqSections.map((section) => (
          <div key={section.section} className="faqSection">
            <h2 className="faqSectionTitle">{section.section}</h2>

            {section.faqs.map((faq, index) => {
              count++;
              const id = count;

              return (
                <div key={id} className="faqItem">
                  <div onClick={() => toggleFAQ(id)} className="faqQuestionRow">
                    <p className="faqQuestionText">{faq.question}</p>
                    <span className="faqToggleIcon">
                      {openIndex === id ? "−" : "+"}
                    </span>
                  </div>

                  {openIndex === id && (
                    <p className="faqAnswer">{faq.answer}</p>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <Footer />
    </div>
  );
}
